import {
  type GetStaticPaths,
  type GetStaticProps,
  type NextPageWithLayout,
} from 'next'
import { type ReactNode } from 'react'
import React from 'react'
import { getPublicLayout } from '@public/components/PublicLayout'
import {
  EXPERT_SHOWROOMS_PAGE_SLUG,
  SHOWROOM_BRANDS_PAGE_SLUG,
} from 'constants/common'
import { ROUTE } from 'constants/routes'
import { CmsShowroomNavigationPageSlugsDocument } from 'types/graphql-generated'
import { initializeApollo } from 'utils/apolloClient'
import { categoryRedirects } from './categoryPagesRedirects'
import { expertShowroomsRedirects } from './expertShowroomsRedirects'
import { useCmsShowroomPageBySlug } from './hooks'
import { prepareGetStaticPropsForShowroomPage } from './prepareGetStaticPropsForShowroomPage'
import { ShowroomPageView } from './ShowroomPageView'

interface ShowroomPageProps {
  slug: string
  isPreview?: boolean
}

export const ShowroomPage: NextPageWithLayout<ShowroomPageProps> = ({
  slug,
  isPreview,
}) => {
  const { pageBySlug, isLoading, widgets } = useCmsShowroomPageBySlug({
    slug,
    isPreview,
  })

  const isShowroomExpertsPage = slug === EXPERT_SHOWROOMS_PAGE_SLUG

  return (
    <ShowroomPageView
      isPreview={isPreview}
      slug={slug}
      data={pageBySlug}
      isLoading={isLoading}
      widgets={widgets}
      canonicalUrl={
        slug === SHOWROOM_BRANDS_PAGE_SLUG
          ? undefined
          : ROUTE.SHOWROOM_DETAIL({ slug })
      }
      isSearchFilterParamsMode
      {...(pageBySlug && {
        fallbackMetaTitle: isShowroomExpertsPage
          ? 'Expert Showrooms - Shop Designer Curated Home Decor'
          : `${pageBySlug.cmsPage.title}`,
        fallbackDescription: isShowroomExpertsPage
          ? 'Get the look of your favorite interior designer by shopping the Showrooms of Experts from Jake Arnold to Jenna Lyons. Discover vintage and new furniture, lighting, rugs, and more.'
          : `Shop ${pageBySlug.cmsPage.title}`,
      })}
    />
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  const apolloClient = initializeApollo()
  const { data: { cmsShowroomNavigationPageSlugs = [] } = {} } =
    await apolloClient.query({
      query: CmsShowroomNavigationPageSlugsDocument,
    })
  const skippedPaths = [...categoryRedirects, ...expertShowroomsRedirects].map(
    ({ source }) => source
  )
  const paths = cmsShowroomNavigationPageSlugs.reduce(
    (filteredPaths, slug: string) => {
      if (skippedPaths.includes(slug)) {
        return filteredPaths
      }
      return [
        ...filteredPaths,
        {
          params: { slug },
        },
      ]
    },
    []
  )
  return {
    paths,
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = (context) => {
  const routeToRedirect = [
    ...categoryRedirects,
    ...expertShowroomsRedirects,
  ].find(({ source }) => source === context.params.slug)

  if (!routeToRedirect) {
    return prepareGetStaticPropsForShowroomPage(context, {
      slug: context.params.slug as string,
      route: ROUTE.SHOWROOM_DETAIL({ slug: context.params.slug as string }),
    })
  }
  return {
    redirect: {
      destination: ROUTE.SHOWROOM_DETAIL({
        slug: routeToRedirect.destination,
      }),
      permanent: false,
    },
  }
}

const PageLayout = (page: ReactNode) =>
  getPublicLayout(page, { isFullWidth: true, isWithBreadcrumbs: true })

ShowroomPage.getLayout = PageLayout
